import type { ItemSummary, SearchHit } from '@mystiq/contracts';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { keys, useWorkspaceId, useWorkspaceInfo, useWorkspaceMutation } from '../api/hooks.ts';
import { useApi } from '../auth/session.tsx';
import { EmptyState, ErrorLine, iconForItem, ItemRow, Modal, Thumb } from '../components/ui.tsx';
import { Activity, ICON, Note, Plus, Restore, Search, Site, Trash } from '../icons.ts';
import { snippetParts, timeAgo } from '../lib/format.ts';

export function NotesPage() {
  const ws = useWorkspaceId();
  const api = useApi();
  const navigate = useNavigate();
  const info = useWorkspaceInfo(ws);
  const notes = useInfiniteQuery({
    queryKey: keys.items(ws, { kind: 'note' }),
    queryFn: ({ pageParam }) => api.items(ws, { kind: 'note', ...(pageParam ? { cursor: pageParam } : {}) }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const create = useWorkspaceMutation(ws, () => api.createNote(ws, {}));
  const rows = notes.data?.pages.flatMap((page) => page.items) ?? [];
  return (
    <div className="page">
      <h1 className="page-title">Notes</h1>
      {info.data?.role !== 'viewer' && (
        <div className="toolbar">
          <button
            className="button button-primary"
            disabled={create.isPending}
            onClick={async () => {
              const note = await create.mutateAsync(undefined).catch(() => null);
              if (note) navigate(`/w/${ws}/items/${note.id}`);
            }}
          >
            <Plus size={ICON.nav} stroke={ICON.stroke} />
            New note
          </button>
        </div>
      )}
      <div className="content stack">
        <ErrorLine error={create.error} />
        <ul className="rows">
          {rows.map((item) => (
            <ItemRow key={item.id} ws={ws} item={item} />
          ))}
        </ul>
        {notes.hasNextPage && (
          <div>
            <button className="button" onClick={() => void notes.fetchNextPage()}>
              Show more
            </button>
          </div>
        )}
        {notes.isSuccess && !rows.length && <EmptyState icon={Note} title="No notes yet">New notes land in Inbox; move them into folders whenever you like.</EmptyState>}
      </div>
    </div>
  );
}

/** Interactive Sites, made from notes and files (or opened from HTML files) on their own pages. */
export function SitesPage() {
  const ws = useWorkspaceId();
  const api = useApi();
  const sites = useInfiniteQuery({
    queryKey: keys.items(ws, { kind: 'site' }),
    queryFn: ({ pageParam }) => api.items(ws, { kind: 'site', ...(pageParam ? { cursor: pageParam } : {}) }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = sites.data?.pages.flatMap((page) => page.items) ?? [];
  return (
    <div className="page">
      <h1 className="page-title">Sites</h1>
      <div className="content stack">
        <ErrorLine error={sites.error} />
        <ul className="rows">
          {rows.map((item) => (
            <ItemRow key={item.id} ws={ws} item={item} />
          ))}
        </ul>
        {sites.hasNextPage && (
          <div>
            <button className="button" onClick={() => void sites.fetchNextPage()}>
              Show more
            </button>
          </div>
        )}
        {sites.isSuccess && !rows.length && (
          <EmptyState icon={Site} title="No Sites yet">
            A Site is an interactive page, such as a quiz, flashcards or a guide. Open a note or file and choose Make a Site, or open an HTML file as a Site.
          </EmptyState>
        )}
      </div>
    </div>
  );
}

export function SearchPage() {
  const ws = useWorkspaceId();
  const api = useApi();
  const [params, setParams] = useSearchParams();
  const [input, setInput] = useState(params.get('q') ?? '');
  const query = params.get('q') ?? '';
  useEffect(() => {
    const timer = setTimeout(() => setParams(input.trim() ? { q: input.trim() } : {}, { replace: true }), 300);
    return () => clearTimeout(timer);
  }, [input, setParams]);
  const results = useQuery({ queryKey: keys.search(ws, query), queryFn: () => api.search(ws, query), enabled: query.length > 0 });
  const hits = results.data?.hits ?? [];
  const meaning = results.data?.meaning;
  return (
    <div className="page">
      <h1 className="page-title">Search</h1>
      <div className="toolbar">
        <input
          className="input"
          type="search"
          aria-label="Search"
          placeholder='Words, "exact phrases", codes, or describe what you need'
          value={input}
          autoFocus
          onChange={(event) => setInput(event.target.value)}
          style={{ maxWidth: 560 }}
        />
      </div>
      <div className="content stack">
        {meaning && meaning !== 'on' && (
          <p className="meta" role="status">
            {meaning === 'off'
              ? 'Searching by words only: search by meaning is off.'
              : 'Some recent changes are still being added to search by meaning. Searching by words already includes them.'}
          </p>
        )}
        <ErrorLine error={results.error} />
        <ul className="rows" aria-label="Search results">
          {groupHits(hits).map(({ hit, texts }) => (
            <li key={hit.item.id}>
              <HitRow ws={ws} hit={hit} />
              {texts.length > 0 && (
                <ul className="rows row-children" aria-label={`Text read from ${hit.item.displayTitle}`}>
                  {texts.map((text) => (
                    <li key={text.item.id}>
                      <HitRow ws={ws} hit={text} nested />
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
        {query && results.isSuccess && !hits.length && (
          <EmptyState icon={Search} title={`Nothing matches "${query}"`}>
            Try other words, or describe what you are looking for. Items in Trash are not searched.
          </EmptyState>
        )}
      </div>
    </div>
  );
}

/** Text read from a file is listed under that file when both are found; each stays its own result. */
function groupHits(hits: SearchHit[]): Array<{ hit: SearchHit; texts: SearchHit[] }> {
  const found = new Set(hits.map((hit) => hit.item.id));
  const underSource = (hit: SearchHit) => (hit.source && found.has(hit.source.itemId) ? hit.source.itemId : null);
  return hits.filter((hit) => !underSource(hit)).map((hit) => ({ hit, texts: hits.filter((other) => underSource(other) === hit.item.id) }));
}

function HitRow({ ws, hit, nested = false }: { ws: string; hit: SearchHit; nested?: boolean }) {
  const source = hit.source && !nested ? hit.source : null;
  const details = [
    hit.match === 'meaning' ? 'Similar meaning' : null,
    hit.location,
    source ? (source.title === null ? 'Read from a file that was deleted' : `Read from ${source.title}${source.available ? '' : ' (in Trash)'}`) : null,
  ].filter(Boolean);
  return (
    <Link className="row" to={`/w/${ws}/items/${hit.item.id}`}>
      <Thumb icon={iconForItem(hit.item)} />
      <span className="row-body">
        <span className="row-title">{hit.item.displayTitle}</span>
        <span className="row-meta">
          {hit.snippet
            ? snippetParts(hit.snippet).map((part, index) => (part.hit ? <mark key={index}>{part.text}</mark> : <span key={index}>{part.text}</span>))
            : (hit.item.description ?? '')}
        </span>
        {details.length > 0 && <span className="row-meta">{details.join(' · ')}</span>}
      </span>
    </Link>
  );
}

export function TrashPage() {
  const ws = useWorkspaceId();
  const api = useApi();
  const info = useWorkspaceInfo(ws);
  const trash = useQuery({ queryKey: keys.items(ws, { view: 'trash' }), queryFn: () => api.items(ws, { view: 'trash' }) });
  const restore = useWorkspaceMutation(ws, (itemId: string) => api.restoreItem(ws, itemId));
  const purge = useWorkspaceMutation(ws, (itemId: string) => api.purgeItem(ws, itemId));
  const [confirming, setConfirming] = useState<ItemSummary | null>(null);
  const isOwner = info.data?.role === 'owner';
  const rows = trash.data?.items ?? [];
  return (
    <div className="page">
      <h1 className="page-title">Trash</h1>
      <p className="meta" style={{ marginTop: 'var(--space-6)' }}>
        Moving something to Trash affects only that item: notes, text and other outputs made from it stay where they are. Restoring puts it back in its folder, or in Inbox if the folder was deleted.
      </p>
      <div className="content stack">
        <ErrorLine error={restore.error ?? purge.error} />
        <ul className="rows">
          {rows.map((item) => (
            <ItemRow
              key={item.id}
              ws={ws}
              item={item}
              extraMeta={item.trashedAt ? `Trashed ${timeAgo(item.trashedAt)}` : undefined}
              actions={
                info.data?.role !== 'viewer' ? (
                  <>
                    <button className="button button-inline" onClick={() => restore.mutate(item.id)} disabled={restore.isPending}>
                      <Restore size={ICON.inline} stroke={ICON.stroke} />
                      Restore
                    </button>
                    {isOwner && (
                      <button className="button button-inline" onClick={() => setConfirming(item)}>
                        Delete permanently
                      </button>
                    )}
                  </>
                ) : undefined
              }
            />
          ))}
        </ul>
        {trash.isSuccess && !rows.length && <EmptyState icon={Trash} title="Trash is empty" />}
      </div>
      {confirming && (
        <Modal title="Delete permanently?" onClose={() => setConfirming(null)}>
          <p>
            <strong>{confirming.displayTitle}</strong> and everything stored for it (its content, original file and AI details) will be erased. This cannot be undone.
          </p>
          <p className="meta">Outputs made from it, such as extracted text and written notes, are separate items and stay, along with whatever they already contain.</p>
          <div className="modal-actions">
            <button
              className="button button-primary"
              disabled={purge.isPending}
              onClick={async () => {
                if (await purge.mutateAsync(confirming.id).then(() => true, () => false)) setConfirming(null);
              }}
            >
              Delete permanently
            </button>
            <button className="button" onClick={() => setConfirming(null)}>
              Cancel
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

const ACTION_WORDS: Record<string, string> = {
  'workspace.created': 'created the workspace',
  'folder.created': 'created a folder',
  'folder.renamed': 'renamed a folder',
  'folder.moved': 'moved a folder',
  'folder.deleted': 'deleted a folder',
  'item.created': 'created an item',
  'item.updated': 'edited details',
  'item.moved': 'moved an item',
  'item.trashed': 'moved an item to Trash',
  'item.restored': 'restored an item',
  'item.purge_requested': 'started deleting an item permanently',
  'item.purged': 'deleted an item permanently',
  'item.content_saved': 'saved changes',
  'item.uploaded': 'uploaded a file',
  'item.upload_failed': 'had an upload fail',
  'item.metadata_generated': 'added AI details',
  'operation.queued': 'queued processing',
  'operation.running': 'started processing',
  'operation.succeeded': 'finished processing',
  'operation.partial': 'finished processing part of an item',
  'operation.failed': 'could not finish processing',
  'operation.retrying': 'is retrying processing',
  'operation.cancelled': 'stopped processing',
  'operation.unsupported': 'skipped an unsupported step',
  'member.role_changed': "changed a member's role",
  'member.removed': 'removed a member',
  'connection.created': 'connected an agent',
  'connection.revoked': 'disconnected an agent',
  'connection.allowed': 'allowed an agent again',
};

export function ActivityPage() {
  const ws = useWorkspaceId();
  const api = useApi();
  const activity = useQuery({ queryKey: keys.activity(ws), queryFn: () => api.activity(ws) });
  const events = (activity.data?.events ?? []).filter((event) => event.action !== 'operation.running' && event.action !== 'operation.queued');
  return (
    <div className="page">
      <h1 className="page-title">Activity</h1>
      <div className="content">
        <ul className="rows">
          {events.map((event) => {
            const who = event.actor.kind === 'system' ? 'Mystiq' : event.actor.kind === 'agent' ? `Agent for ${event.actor.displayName ?? 'a member'}` : (event.actor.displayName ?? 'A member');
            const target = event.targetKind === 'item' && event.targetId ? (
              <Link to={`/w/${ws}/items/${event.targetId}`}>Open</Link>
            ) : null;
            return (
              <li className="row" key={event.seq}>
                <span className="thumb" aria-hidden>
                  <Activity size={ICON.empty} stroke={ICON.stroke} />
                </span>
                <span className="row-body">
                  <span className="row-title">
                    {who} {ACTION_WORDS[event.action] ?? event.action}
                  </span>
                  <span className="row-meta">
                    {timeAgo(event.createdAt)} {target && <>· {target}</>}
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
        {activity.isSuccess && !events.length && <EmptyState icon={Activity} title="No activity yet" />}
      </div>
    </div>
  );
}
